import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { Toaster } from "sonner";
import { getCategoryBySlug } from "./data/category-display";
import { BrandLandingPage } from "./pages/BrandLandingPage";
import { BrandPickerPage } from "./pages/BrandPickerPage";
import { CategoryChatPage } from "./pages/CategoryChatPage";

// Root layout
const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <Toaster position="top-center" richColors />
    </>
  ),
});

// Brand picker at /
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: BrandPickerPage,
});

// Brand landing at /$brand
const brandRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$brand",
  beforeLoad: ({ params }) => {
    if (params.brand !== "hims" && params.brand !== "hers") {
      throw redirect({ to: "/" });
    }
  },
  component: BrandLandingPage,
});

// Category chat at /$brand/$cat
const categoryChatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$brand/$cat",
  beforeLoad: ({ params }) => {
    if (params.brand !== "hims" && params.brand !== "hers") {
      throw redirect({ to: "/" });
    }
    if (!getCategoryBySlug(params.cat)) {
      throw redirect({ to: "/$brand", params: { brand: params.brand } });
    }
  },
  component: CategoryChatPage,
});

// Claims preview - only defined when VITE_ENABLE_DEBUG_COMPONENTS=true
const claimsRoute =
  import.meta.env.VITE_ENABLE_DEBUG_COMPONENTS === "true"
    ? createRoute({
        getParentRoute: () => rootRoute,
        path: "claims",
        component: lazyRouteComponent(() => import("./pages/ClaimsPage"), "ClaimsPage"),
      })
    : null;

// Mock chat - only defined when VITE_ENABLE_DEBUG_COMPONENTS=true
const mockChatRoute =
  import.meta.env.VITE_ENABLE_DEBUG_COMPONENTS === "true"
    ? createRoute({
        getParentRoute: () => rootRoute,
        path: "mock-chat",
        component: lazyRouteComponent(() => import("./pages/MockChatPage"), "MockChatPage"),
      })
    : null;

// Docs routes - only defined when VITE_ENABLE_DEBUG_COMPONENTS=true
// Vite tree-shakes this entire block when the condition is false at build time
const docsRoutes =
  import.meta.env.VITE_ENABLE_DEBUG_COMPONENTS === "true"
    ? (() => {
        // Docs parent route with lazy-loaded layout
        const docsRoute = createRoute({
          getParentRoute: () => rootRoute,
          path: "docs",
          component: lazyRouteComponent(() => import("./pages/docs/DocsLayout"), "DocsLayout"),
        });

        // Docs index route
        const docsIndexRoute = createRoute({
          getParentRoute: () => docsRoute,
          path: "/",
          component: lazyRouteComponent(
            () => import("./pages/docs/DocsIndexPage"),
            "DocsIndexPage",
          ),
        });

        // Widgets page
        const docsWidgetsRoute = createRoute({
          getParentRoute: () => docsRoute,
          path: "widgets",
          component: lazyRouteComponent(() => import("./pages/docs/WidgetsPage"), "WidgetsPage"),
        });

        // Docs catch-all
        const docsSlugRoute = createRoute({
          getParentRoute: () => docsRoute,
          path: "$",
          component: lazyRouteComponent(() => import("./pages/docs/DocsSlugPage"), "DocsSlugPage"),
        });

        return docsRoute.addChildren([docsIndexRoute, docsWidgetsRoute, docsSlugRoute]);
      })()
    : null;

// Dev Quality Console route — only defined when VITE_ENABLE_DEBUG_COMPONENTS=true.
// Plan: docs/plans/2026-04-21-dev-quality-console.md
const devQualityRoute =
  import.meta.env.VITE_ENABLE_DEBUG_COMPONENTS === "true"
    ? createRoute({
        getParentRoute: () => rootRoute,
        path: "dev/quality",
        component: lazyRouteComponent(
          () => import("./pages/dev-quality/DevQualityPage"),
          "DevQualityPage",
        ),
      })
    : null;

// Build route tree with proper nesting
const routeTree = rootRoute.addChildren([
  indexRoute,
  brandRoute,
  categoryChatRoute,
  ...(claimsRoute ? [claimsRoute] : []),
  ...(mockChatRoute ? [mockChatRoute] : []),
  ...(docsRoutes ? [docsRoutes] : []),
  ...(devQualityRoute ? [devQualityRoute] : []),
]);

// Create router
export const router = createRouter({ routeTree });

// Type registration for TypeScript
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
